/**
 * useDateRange Hook (Stories 5.8, 5.9)
 *
 * Manages date range state for report generation.
 * Lives as a sibling to useUndoStack (canvas layout) — NOT in the undo stack.
 * Date range is report-level metadata; Ctrl+Z should not undo date changes.
 *
 * Story 5.9 adds comparison period support: toggle, mode selection,
 * and auto-recalculation when primary range or comparison mode changes.
 */

import { useState, useCallback } from "react";
import {
  calculatePresetRange,
  calculateComparisonRange,
  validateDateRange,
  isComparisonWithinPlanLimit,
  type DateRangePresetKey,
  type DateRangeValidation,
  type ComparisonMode,
} from "../lib/dateRangeUtils";

export interface DateRangeState {
  presetKey: DateRangePresetKey | null;
  start: string;
  end: string;
  comparisonEnabled: boolean;
  comparisonStart: string | null;
  comparisonEnd: string | null;
  comparisonMode: ComparisonMode;
}

export interface DateRangeValidationExtended extends DateRangeValidation {
  comparisonWarning?: string;
}

/**
 * Saved date-range snapshot used to seed the hook from a persisted report
 * (Story 37.1). `start`/`end` are the primary window; the comparison pair is
 * optional and, when present, is restored VERBATIM (never recomputed).
 */
export interface DateRangeHydration {
  start: string;
  end: string;
  comparisonStart?: string | null;
  comparisonEnd?: string | null;
}

export interface UseDateRangeResult {
  state: DateRangeState;
  selectPreset: (key: DateRangePresetKey) => void;
  selectCustomRange: (start: string, end: string) => void;
  validate: () => DateRangeValidationExtended;
  toggleComparison: () => void;
  setComparisonMode: (mode: ComparisonMode) => void;
  /**
   * Seed the date range from a saved report (Story 37.1). Sets the primary
   * range to "custom" and the supplied start/end. When BOTH comparison dates
   * are present, comparison is enabled and the dates are set EXACTLY as saved
   * (no `recalcComparison`) so asymmetric/custom windows survive the round-trip.
   * When comparison dates are absent, comparison stays disabled.
   */
  hydrate: (saved: DateRangeHydration) => void;
}

/** Compute default state lazily so "today" is fresh at hook invocation, not module load. */
function createDefaultState(): DateRangeState {
  const range = calculatePresetRange("last-30");
  return {
    presetKey: "last-30",
    start: range.start,
    end: range.end,
    comparisonEnabled: false,
    comparisonStart: null,
    comparisonEnd: null,
    comparisonMode: "previous-period",
  };
}

/** Recalculate comparison dates from primary range and mode. */
function recalcComparison(
  start: string,
  end: string,
  mode: ComparisonMode,
  enabled: boolean
): { comparisonStart: string | null; comparisonEnd: string | null } {
  if (!enabled || !start || !end) {
    return { comparisonStart: null, comparisonEnd: null };
  }
  const range = calculateComparisonRange(start, end, mode);
  return { comparisonStart: range.start, comparisonEnd: range.end };
}

export function useDateRange(): UseDateRangeResult {
  const [state, setState] = useState<DateRangeState>(createDefaultState);

  const selectPreset = useCallback((key: DateRangePresetKey) => {
    if (key === "custom") {
      // Switch to custom mode but keep current dates so user can adjust
      setState((prev) => ({ ...prev, presetKey: "custom" }));
      return;
    }

    const range = calculatePresetRange(key);
    setState((prev) => {
      const comparison = recalcComparison(
        range.start,
        range.end,
        prev.comparisonMode,
        prev.comparisonEnabled
      );
      return {
        ...prev,
        presetKey: key,
        start: range.start,
        end: range.end,
        ...comparison,
      };
    });
  }, []);

  const selectCustomRange = useCallback((start: string, end: string) => {
    // Auto-swap if end < start (AC #4)
    const [s, e] = start > end ? [end, start] : [start, end];
    setState((prev) => {
      const comparison = recalcComparison(
        s,
        e,
        prev.comparisonMode,
        prev.comparisonEnabled
      );
      return {
        ...prev,
        presetKey: "custom",
        start: s,
        end: e,
        ...comparison,
      };
    });
  }, []);

  const toggleComparison = useCallback(() => {
    setState((prev) => {
      const newEnabled = !prev.comparisonEnabled;
      const comparison = recalcComparison(
        prev.start,
        prev.end,
        prev.comparisonMode,
        newEnabled
      );
      return {
        ...prev,
        comparisonEnabled: newEnabled,
        ...comparison,
      };
    });
  }, []);

  const setComparisonMode = useCallback((mode: ComparisonMode) => {
    setState((prev) => {
      const comparison = recalcComparison(
        prev.start,
        prev.end,
        mode,
        prev.comparisonEnabled
      );
      return {
        ...prev,
        comparisonMode: mode,
        ...comparison,
      };
    });
  }, []);

  const hydrate = useCallback((saved: DateRangeHydration) => {
    const hasComparison = Boolean(saved.comparisonStart && saved.comparisonEnd);
    setState((prev) => ({
      ...prev,
      presetKey: "custom",
      start: saved.start,
      end: saved.end,
      comparisonEnabled: hasComparison,
      // Restore VERBATIM — do not run recalcComparison, which would overwrite a
      // saved asymmetric/custom window with a freshly computed one (Story 37.1).
      comparisonStart: hasComparison ? saved.comparisonStart ?? null : null,
      comparisonEnd: hasComparison ? saved.comparisonEnd ?? null : null,
      // `comparisonMode` only feeds `recalcComparison`, which we deliberately do
      // NOT call here — so the saved dates are authoritative regardless of mode.
      // Preserve the prior mode rather than inventing one (the union has no
      // "custom" member; that value belongs to DateRangePresetKey, not here).
      comparisonMode: prev.comparisonMode,
    }));
  }, []);

  const validate = useCallback(
    (): DateRangeValidationExtended => {
      const base = validateDateRange(state.start, state.end);
      if (!base.valid) return base;

      // Add comparison-specific warning (non-blocking)
      if (
        state.comparisonEnabled &&
        state.comparisonStart &&
        !isComparisonWithinPlanLimit(state.comparisonStart)
      ) {
        return {
          ...base,
          comparisonWarning: `Comparison period starts outside the history window.`,
        };
      }

      return base;
    },
    [state.start, state.end, state.comparisonEnabled, state.comparisonStart]
  );

  return {
    state,
    selectPreset,
    selectCustomRange,
    validate,
    toggleComparison,
    setComparisonMode,
    hydrate,
  };
}
